import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import sensitiveWords from '../../api/sensitiveWords';

const state = {
  records: [],
  meta: {
    total: 0,
    perPage: 10,
    currentPage: 1,
  },
  categories: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getRecords: _state => _state.records,
  getMeta: _state => _state.meta,
  getCategories: _state => _state.categories,
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  async fetch({ commit }, { search, page, perPage }) {
    commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isFetching: true });
    try {
      const response = await sensitiveWords.get({ search, page, perPage });
      commit(types.SET_SENSITIVE_WORDS, response.data.words);
      commit(types.SET_SENSITIVE_WORDS_META, response.data.meta);
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isFetching: false });
    } catch (error) {
      // Handle error
    } finally {
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isFetching: false });
    }
  },

  async fetchCategories({ commit }) {
    const response = await sensitiveWords.categories();
    commit(types.SET_SENSITIVE_CATEGORIES, response.data.categories);
  },

  async create({ commit }, data) {
    commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isCreating: true });
    try {
      const response = await sensitiveWords.create(data);
      commit(types.ADD_SENSITIVE_WORDS, response.data);
      commit(types.UPDATE_SENSITIVE_WORDS_META, true);
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isCreating: false });
    } finally {
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isCreating: false });
    }
  },

  async import({ commit }, file) {
    commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isCreating: true });
    try {
      await sensitiveWords.import(file);
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isCreating: false });
    } catch (error) {
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isCreating: false });
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data);
      }
    }
  },

  async update({ commit }, { id, ...data }) {
    commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isUpdating: true });
    try {
      const response = await sensitiveWords.update(id, data);
      commit(types.EDIT_SENSITIVE_WORDS, response.data);
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isUpdating: false });
    } finally {
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isDeleting: true });
    try {
      await sensitiveWords.delete(id);
      commit(types.DELETE_SENSITIVE_WORDS, id);
      commit(types.UPDATE_SENSITIVE_WORDS_META, false);
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isDeleting: false });
    } finally {
      commit(types.SET_SENSITIVE_WORDS_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_SENSITIVE_WORDS_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_SENSITIVE_WORDS_META]: (_state, data) => {
    _state.meta = {
      ..._state.meta,
      ...data,
    };
  },
  [types.UPDATE_SENSITIVE_WORDS_META]: (_state, flag) => {
    if (flag) {
      _state.meta.total += 1;
    } else {
      _state.meta.total -= 1;
    }
  },
  [types.SET_SENSITIVE_CATEGORIES]: (_state, data) => {
    _state.categories = data;
  },

  [types.SET_SENSITIVE_WORDS]: MutationHelpers.set,
  [types.ADD_SENSITIVE_WORDS]: MutationHelpers.create,
  [types.EDIT_SENSITIVE_WORDS]: MutationHelpers.update,
  [types.DELETE_SENSITIVE_WORDS]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
